interface UpdateElements {
  button: HTMLButtonElement
  status: HTMLElement
  dialog: HTMLDialogElement
}

// Only this document's explicit consent grants reload permission. Worker activation
// is registration-wide; neither discovery nor another tab's activation grants it.
export function initializeUpdates(
  elements: UpdateElements,
  registration: Promise<ServiceWorkerRegistration>,
  container: ServiceWorkerContainer = navigator.serviceWorker,
  reload: () => void = () => location.reload(),
): void {
  const { button, status, dialog } = elements
  let registered: ServiceWorkerRegistration | null = null
  let checking = false
  let checkReturned = false
  let checkId = 0
  let deadline: ReturnType<typeof setTimeout> | undefined
  let deadlineAt = 0
  let offered: ServiceWorker | null = null
  let confirming: ServiceWorker | null = null
  let attempt: { target: ServiceWorker; previous: ServiceWorker } | null = null
  const observed = new WeakSet<ServiceWorker>()

  const controlled = () => container.controller !== null && container.controller === registered?.active
  const waiting = () => controlled() && registered?.waiting?.state === 'installed' ? registered.waiting : null
  const render = () => {
    button.disabled = checking
    button.textContent = attempt ? 'Cancel restart' : offered ? 'Update now' : 'Check for updates'
  }
  const stop = (message: string) => {
    clearTimeout(deadline)
    checkId += 1
    checking = false
    attempt = null
    offered = null
    status.textContent = message
    render()
  }
  const reconcile = () => {
    for (const worker of [registered?.installing, registered?.waiting, registered?.active]) {
      if (worker && !observed.has(worker)) {
        observed.add(worker)
        worker.addEventListener('statechange', reconcile)
      }
    }
    if (attempt) {
      const { target, previous } = attempt
      if (Date.now() >= deadlineAt) {
        stop('The update timed out. This page will not reload. Check again when ready.')
      } else if (target.state === 'redundant' || registered?.installing
        || (target.state === 'installed' && registered?.waiting !== target)
        || (container.controller !== previous && container.controller !== target)) {
        stop('The update changed. Check again when ready.')
      } else if (container.controller === target && target.state === 'activated') {
        stop('Restarting…')
        reload()
      }
      return
    }
    if (confirming && waiting() !== confirming) {
      confirming = null
      dialog.close()
      status.textContent = 'The update changed. Check again when ready.'
    }
    if (checking && Date.now() >= deadlineAt) {
      stop('The update check timed out. Try again when online.')
      return
    }
    if (checking && checkReturned && !registered?.installing) {
      clearTimeout(deadline)
      checking = false
      offered = waiting()
      status.textContent = offered ? 'An update is ready.' : controlled()
        ? 'No update is ready.' : 'Close and reopen online to finish setting up updates.'
    } else if (offered && waiting() !== offered) {
      offered = null
      status.textContent = 'The update changed. Check again when ready.'
    }
    render()
  }
  container.addEventListener('controllerchange', reconcile)
  void registration.then((value) => {
    registered = value
    value.addEventListener('updatefound', reconcile)
    reconcile()
  }).catch(() => { stop('Updates are unavailable. Close and reopen online to try again.') })

  button.hidden = false
  button.addEventListener('click', () => {
    if (attempt) {
      stop('Restart cancelled. The update may still activate; this page will not reload.')
      return
    }
    if (offered) {
      if (waiting() !== offered || registered?.installing) {
        stop('The update changed. Check again when ready.')
        return
      }
      confirming = offered
      dialog.returnValue = ''
      dialog.showModal()
      dialog.querySelector<HTMLButtonElement>('[value="cancel"]')?.focus()
      return
    }
    if (!registered) {
      status.textContent = 'Updates are not ready. Try again later, or close and reopen online.'
      return
    }
    if (!controlled()) {
      status.textContent = 'Close and reopen online to finish setting up updates.'
      return
    }
    checking = true
    checkReturned = false
    const id = ++checkId
    status.textContent = 'Checking for updates…'
    render()
    deadlineAt = Date.now() + 30_000
    deadline = setTimeout(() => stop('The update check timed out. Try again when online.'), 30_000)
    // Observe installation as well as the update() promise: resolving update()
    // alone does not mean its worker has finished downloading.
    void registered.update().then(() => {
      if (id !== checkId || !checking) return
      checkReturned = true
      reconcile()
    }).catch(() => {
      if (id === checkId) stop('Could not check for updates. Try again when online.')
    })
  })
  dialog.addEventListener('close', () => {
    const target = confirming
    confirming = null
    if (dialog.returnValue !== 'update' || !target) return
    if (waiting() !== target || registered?.installing || !container.controller) {
      stop('The update changed. Check again when ready.')
      return
    }
    attempt = { target, previous: container.controller }
    status.textContent = 'Updating… This page will restart.'
    render()
    deadlineAt = Date.now() + 30_000
    deadline = setTimeout(() => stop('The update timed out. This page will not reload. Check again when ready.'), 30_000)
    // Both listeners are already attached. Reconcile again after the message to
    // cover either event ordering, including an immediate activation.
    try {
      target.postMessage({ type: 'SKIP_WAITING' })
      reconcile()
    } catch {
      stop('Could not apply the update. Check again when ready.')
    }
  })
}
